import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import axios from '../../axios';
import logo from '../../img/nekrodruk-logo-dark.png';
import PasswordTextField from '../PasswordTextField';
import { useAuth } from '../../context/AuthContext';
import './LoginForm.scss';

const validationSchema = Yup.object({
  newPassword: Yup.string()
    .min(6, 'Hasło musi mieć co najmniej 6 znaków')
    .max(20, 'Hasło może mieć maksymalnie 20 znaków')
    .matches(
      /^(?=.*[0-9])(?=.*[!@#$%^&*])/,
      'Hasło musi zawierać cyfrę i znak specjalny',
    )
    .required('Nowe hasło jest wymagane'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Hasła muszą być takie same')
    .required('Powtórzenie hasła jest wymagane'),
});

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const { setIsLoggedIn } = useAuth();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const token = searchParams.get('token') || '';
  const hasValidTokenShape = /^[a-f0-9]{64}$/i.test(token);

  const handleSubmit = async (values, { setSubmitting }) => {
    setMessage('');
    setError('');

    try {
      const response = await axios.post('/password-reset/confirm', {
        token,
        newPassword: values.newPassword,
      });
      localStorage.removeItem('token');
      setIsLoggedIn(false);
      setMessage(response.data.message);
    } catch (requestError) {
      setError(
        requestError.response?.data?.message
          || 'Nie udało się zmienić hasła. Spróbuj ponownie później.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className='LoginForm passwordResetPage'>
      <Paper elevation={0} className='formCard'>
        <Stack spacing={3} alignItems='center'>
          <Box component='img' src={logo} alt='Nekrodruk' className='logo' />
          <Typography component='h1' variant='h4' className='formTitle' align='center'>
            Nowe hasło
          </Typography>

          {!hasValidTokenShape && (
            <Alert severity='error' className='formAlert'>
              Link do zmiany hasła jest nieprawidłowy lub niekompletny.
            </Alert>
          )}

          {hasValidTokenShape && !message && (
            <Formik
              initialValues={{ newPassword: '', confirmPassword: '' }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form className='formContainer'>
                  <Stack spacing={2}>
                    <Field name='newPassword'>
                      {({ field, meta }) => (
                        <PasswordTextField
                          {...field}
                          autoComplete='new-password'
                          label='Nowe hasło'
                          fullWidth
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error ? meta.error : ''}
                        />
                      )}
                    </Field>
                    <Field name='confirmPassword'>
                      {({ field, meta }) => (
                        <PasswordTextField
                          {...field}
                          autoComplete='new-password'
                          label='Powtórz nowe hasło'
                          fullWidth
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error ? meta.error : ''}
                        />
                      )}
                    </Field>
                    {error && <Alert severity='error' className='formAlert'>{error}</Alert>}
                    <Button
                      type='submit'
                      variant='contained'
                      size='large'
                      disabled={isSubmitting}
                      className='formSubmit'
                    >
                      Zmień hasło
                    </Button>
                  </Stack>
                </Form>
              )}
            </Formik>
          )}

          {message && <Alert severity='success' className='formAlert'>{message}</Alert>}

          <Link component={RouterLink} to={message ? '/login' : '/forgot-password'}>
            {message ? 'Przejdź do logowania' : 'Wyślij nowy link'}
          </Link>
        </Stack>
      </Paper>
    </Box>
  );
}
